import { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface Category {
  id: string;
  name: string;
  order: number;
}

const CategoryManager = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const categoriesQuery = query(
      collection(db, 'layerCategories'),
      orderBy('order', 'asc')
    );

    const unsubscribe = onSnapshot(categoriesQuery, (snapshot) => {
      const fetchedCategories: Category[] = [];
      snapshot.forEach((doc) => {
        fetchedCategories.push({ id: doc.id, ...doc.data() } as Category);
      });
      setCategories(fetchedCategories);
    });

    return () => unsubscribe();
  }, []);

  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;

    try {
      setError('');
      await addDoc(collection(db, 'layerCategories'), {
        name: newCategoryName.trim(),
        order: categories.length,
        createdAt: new Date(),
      });
      setNewCategoryName('');
      setIsAdding(false);
    } catch (err: any) {
      setError(`Failed to add category: ${err.message}`);
    }
  };

  const handleUpdateCategory = async (id: string) => {
    if (!editingName.trim()) return;

    try {
      setError('');
      const categoryRef = doc(db, 'layerCategories', id);
      await updateDoc(categoryRef, {
        name: editingName.trim(),
        updatedAt: new Date(),
      });
      setEditingId(null);
      setEditingName('');
    } catch (err: any) {
      setError(`Failed to update category: ${err.message}`);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!confirm('Are you sure you want to delete this category? Layers in this category will become uncategorized.')) {
      return;
    }

    try {
      setError('');
      const categoryRef = doc(db, 'layerCategories', id);
      await deleteDoc(categoryRef);
    } catch (err: any) {
      setError(`Failed to delete category: ${err.message}`);
    }
  };

  const handleMoveCategory = async (id: string, direction: 'up' | 'down') => {
    const currentIndex = categories.findIndex((cat) => cat.id === id);
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === categories.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const currentCategory = categories[currentIndex];
    const otherCategory = categories[newIndex];

    try {
      setError('');
      // Swap order values
      await updateDoc(doc(db, 'layerCategories', currentCategory.id), { order: newIndex });
      await updateDoc(doc(db, 'layerCategories', otherCategory.id), { order: currentIndex });
    } catch (err: any) {
      setError(`Failed to reorder categories: ${err.message}`);
    }
  };

  return (
    <div className="category-manager">
      <div className="manager-header">
        <h3>Layer Categories</h3>
        <button
          className="btn-small btn-primary"
          onClick={() => setIsAdding(true)}
          disabled={isAdding}
        >
          + Add Category
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {isAdding && (
        <form onSubmit={handleAddCategory} className="add-form">
          <input
            type="text"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            placeholder="Category name (e.g., Sentinel Data)"
            className="form-input"
            autoFocus
          />
          <div className="form-actions">
            <button type="submit" className="btn-small btn-primary">
              Save
            </button>
            <button
              type="button"
              className="btn-small btn-secondary"
              onClick={() => {
                setIsAdding(false);
                setNewCategoryName('');
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="categories-list">
        {categories.length === 0 ? (
          <p className="empty-message">No categories yet. Add one to get started!</p>
        ) : (
          categories.map((category, index) => (
            <div key={category.id} className="category-item">
              {editingId === category.id ? (
                <div className="edit-form">
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    className="form-input"
                    autoFocus
                  />
                  <div className="form-actions">
                    <button
                      className="btn-small btn-primary"
                      onClick={() => handleUpdateCategory(category.id)}
                    >
                      Save
                    </button>
                    <button
                      className="btn-small btn-secondary"
                      onClick={() => {
                        setEditingId(null);
                        setEditingName('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="category-info">
                    <span className="category-name">{category.name}</span>
                  </div>
                  <div className="category-actions">
                    <button
                      className="btn-icon"
                      onClick={() => handleMoveCategory(category.id, 'up')}
                      disabled={index === 0}
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleMoveCategory(category.id, 'down')}
                      disabled={index === categories.length - 1}
                      title="Move down"
                    >
                      ↓
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => {
                        setEditingId(category.id);
                        setEditingName(category.name);
                      }}
                      title="Edit"
                    >
                      ✎
                    </button>
                    <button
                      className="btn-icon btn-danger"
                      onClick={() => handleDeleteCategory(category.id)}
                      title="Delete"
                    >
                      ×
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CategoryManager;
